import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import InventoryNavigation from 'components/inventory/InventoryNavigation';
import Filter from 'components/common/Filter';
import VendorsTable from 'components/settings/VendorsTable';
import VendorCreateEditDialog from 'components/vendor/VendorCreateEditDialog';

import {
  fetchVendors,
  clearVendors,
} from 'redux/modules/vendors';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class Vendors extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearVendors();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { limit, offset } = this.state;
    const filtersChanged = nextProps.filters['Vendor Name'] !== filters['Vendor Name'];
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }

    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, vendors, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Vendor Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (vendors.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchVendors({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.vendors.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { vendors, count, loading, location, filters } = this.props;
    const { createDialogOpen, mobileFilterExpanded } = this.state;
    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Vendors
          </title>
        </Helmet>
        <InventoryNavigation location={location} />
        <Filter
          form="vendorsFilter"
          placeholder="Search Vendors"
          primaryButtonText="Add New"
          onPrimaryButtonClick={this.onFilterButtonClick}
          beforeTable
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={vendors}
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <VendorsTable
          items={vendors}
          count={count}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <VendorCreateEditDialog
          form="vendorCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

Vendors.propTypes = {
  vendors: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Vendor Name': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchVendors: PropTypes.func.isRequired,
    clearVendors: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

const mapStateToProps = (store, ownProps) => {
  const vendorsFilterValues = getFormValues('vendorsFilter')(store);
  return {
    vendors: store.vendors.data,
    count: store.vendors.count,
    filters: {
      ...vendorsFilterValues
        && vendorsFilterValues.Filter
        ? { 'Vendor Name': vendorsFilterValues.Filter }
        : {},
    },
    loading: store.vendors.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchVendors,
    clearVendors,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(Vendors));



// WEBPACK FOOTER //
// ./src/pages/inventory/Vendors.js